import React, { useState, useEffect } from 'react';
import { goBackOrHome } from '@/lib/navHelpers';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Upload, Video, FileText, Play, Download, Trash2, Edit, Plus, BookOpen, Shield, AlertTriangle, ArrowLeft, Home } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Link, useNavigate } from 'react-router-dom';

interface Course {
  id: string;
  title: string;
  description: string;
  category: string;
  video_url: string | null;
  workbook_url: string | null;
  thumbnail_url: string | null;
  duration_minutes: number | null;
  difficulty_level: string;
  tags: string[];
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

const Courses = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingWorkbook, setUploadingWorkbook] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    difficulty_level: 'beginner',
    duration_minutes: '',
    tags: '',
  });

  useEffect(() => {
    checkAdminStatus();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadCourses();
    }
  }, [isAdmin]);

  const checkAdminStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setCheckingAdmin(false);
        return;
      }

      setCurrentUser(user); // Store user for displaying ID

      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();

      if (error) {
        setIsAdmin(false);
      } else {
        setIsAdmin(!!data);
      }
    } catch (error) {
      setIsAdmin(false);
    } finally {
      setCheckingAdmin(false);
    }
  };

  const loadCourses = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const courseData = {
        user_id: user.id,
        title: formData.title,
        description: formData.description,
        category: formData.category,
        difficulty_level: formData.difficulty_level,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : [],
        is_published: false
      };

      const { data, error } = await supabase
        .from('courses')
        .insert([courseData])
        .select()
        .single();

      if (error) throw error;

      setCourses([data, ...courses]);
      setShowCreateDialog(false);
      setFormData({
        title: '',
        description: '',
        category: 'general',
        difficulty_level: 'beginner',
        duration_minutes: '',
        tags: '',
      });
      toast.success('Course created successfully!');
    } catch (error) {
      toast.error('Failed to create course');
    }
  };

  const handleFileUpload = async (file: File, courseId: string, type: 'video' | 'workbook') => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      if (type === 'video') setUploadingVideo(true);
      if (type === 'workbook') setUploadingWorkbook(true);

      const bucket = type === 'video' ? 'course-videos' : 'course-workbooks';
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${courseId}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(fileName);

      const updateField = type === 'video' ? 'video_url' : 'workbook_url';
      
      const { error: updateError } = await supabase
        .from('courses')
        .update({ [updateField]: publicUrl } as any)
        .eq('id', courseId);

      if (updateError) throw updateError;

      // Update local state
      setCourses(courses.map(course => 
        course.id === courseId 
          ? { ...course, [updateField]: publicUrl }
          : course
      ));

      toast.success(`${type === 'video' ? 'Video' : 'Workbook'} uploaded successfully!`);
    } catch (error) {
      toast.error(`Failed to upload ${type}`);
    } finally {
      if (type === 'video') setUploadingVideo(false);
      if (type === 'workbook') setUploadingWorkbook(false);
    }
  };

  const handleDeleteCourse = async (courseId: string) => {
    if (!confirm('Are you sure you want to delete this course?')) return;

    try {
      const { error } = await supabase
        .from('courses')
        .delete()
        .eq('id', courseId);

      if (error) throw error;

      setCourses(courses.filter(course => course.id !== courseId));
      toast.success('Course deleted successfully!');
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const togglePublishStatus = async (courseId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('courses')
        .update({ is_published: !currentStatus })
        .eq('id', courseId);

      if (error) throw error;

      setCourses(courses.map(course => 
        course.id === courseId 
          ? { ...course, is_published: !currentStatus }
          : course
      ));

      toast.success(`Course ${!currentStatus ? 'published' : 'unpublished'} successfully!`);
    } catch (error) {
      toast.error('Failed to update course status');
    }
  };

  if (checkingAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full p-8">
          <div className="text-center mb-6">
            <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-light text-foreground mb-4">Admin Access Required</h2>
            <p className="text-muted-foreground mb-6">
              You need admin privileges to upload and manage courses.
            </p>
          </div>

          {currentUser && (
            <div className="mb-6 p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">Your User ID:</p>
              <div className="bg-background p-3 rounded border font-mono text-sm break-all">
                {currentUser.id}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Copy this ID and use it in the SQL query below
              </p>
            </div>
          )}

          <div className="mb-6 p-4 bg-muted rounded-lg">
            <p className="text-sm font-medium mb-3">Run this SQL query in Supabase:</p>
            <div className="bg-background p-3 rounded border font-mono text-sm">
              <div>INSERT INTO public.user_roles (user_id, role)</div>
              <div>VALUES ('{currentUser?.id || 'YOUR_USER_ID_HERE'}', 'admin'::app_role);</div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Replace YOUR_USER_ID_HERE with your actual User ID from above if not filled automatically
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => window.open(`https://supabase.com/dashboard/project/jbdsdzmyqkfbqijnoqmo/sql/new`, '_blank')}
              className="flex-1"
            >
              <Shield className="h-4 w-4 mr-2" />
              Open Supabase SQL Editor
            </Button>
            <Link to="/course-library" className="flex-1">
              <Button variant="outline" className="w-full">
                <BookOpen className="h-4 w-4 mr-2" />
                Browse Course Library
              </Button>
            </Link>
          </div>
          
          <p className="text-sm text-muted-foreground mt-4 text-center">
            After running the SQL query, refresh this page to access the admin interface.
          </p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }
          return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-6xl mx-auto">
          {/* Navigation */}
          <div className="flex items-center gap-2 mb-6">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => goBackOrHome(navigate)}
              className="flex items-center gap-2"
            >
              <Home className="h-4 w-4" />
              <span className="hidden sm:inline">Home</span>
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2"
            >
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Admin Portal</span>
            </Button>
          </div>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light text-foreground">Admin: Course Management</h1>
              </div>
              <p className="text-sm sm:text-base lg:text-lg text-muted-foreground">Upload video courses and workbooks for your users to learn from.</p>
            </div>
            
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Create Course
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create New Course</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateCourse} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Course Title</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      placeholder="Enter course title"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Describe your course"
                      rows={3}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category">Category</Label>
                      <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="meditation">Meditation</SelectItem>
                          <SelectItem value="healing">Healing</SelectItem>
                          <SelectItem value="spiritual">Spiritual</SelectItem>
                          <SelectItem value="wellness">Wellness</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="difficulty">Difficulty</Label>
                      <Select value={formData.difficulty_level} onValueChange={(value) => setFormData({...formData, difficulty_level: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">Beginner</SelectItem>
                          <SelectItem value="intermediate">Intermediate</SelectItem>
                          <SelectItem value="advanced">Advanced</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="duration">Duration (minutes)</Label>
                      <Input
                        id="duration"
                        type="number"
                        value={formData.duration_minutes}
                        onChange={(e) => setFormData({...formData, duration_minutes: e.target.value})}
                        placeholder="e.g., 30"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="tags">Tags (comma-separated)</Label>
                      <Input
                        id="tags"
                        value={formData.tags}
                        onChange={(e) => setFormData({...formData, tags: e.target.value})}
                        placeholder="meditation, healing, wellness"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">Create Course</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Courses Grid */}
          {courses.length === 0 ? (
            <Card className="p-12 text-center">
              <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-light text-foreground mb-2">No courses yet</h3>
              <p className="text-muted-foreground mb-4">Create your first wellness course to get started.</p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Course
              </Button>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course) => (
                <Card key={course.id} className="p-6 shadow-therapy border-0">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-foreground mb-2">{course.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{course.description}</p>
                      
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="secondary">{course.category}</Badge>
                        <Badge variant="outline">{course.difficulty_level}</Badge>
                        {course.is_published && <Badge className="bg-green-100 text-green-800">Published</Badge>}
                      </div>
                      
                      {course.duration_minutes && (
                        <p className="text-xs text-muted-foreground">{course.duration_minutes} minutes</p>
                      )}
                    </div>
                    
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => togglePublishStatus(course.id, course.is_published)}
                      >
                        {course.is_published ? 'Unpublish' : 'Publish'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteCourse(course.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Video Upload Section */}
                  <div className="space-y-3">
                    <div className="border-2 border-dashed border-border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Video className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium">Course Video</span>
                      </div>
                      
                      {course.video_url ? (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Video uploaded</span>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" asChild>
                              <a href={course.video_url} target="_blank" rel="noopener noreferrer">
                                <Play className="h-3 w-3" />
                              </a>
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <input
                            type="file"
                            accept="video/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleFileUpload(file, course.id, 'video');
                            }}
                            className="hidden"
                            id={`video-${course.id}`}
                          />
                          <label htmlFor={`video-${course.id}`}>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="w-full" 
                              disabled={uploadingVideo}
                              asChild
                            >
                              <span>
                                <Upload className="h-3 w-3 mr-1" />
                                {uploadingVideo ? 'Uploading...' : 'Upload Video'}
                              </span>
                            </Button>
                          </label>
                        </div>
                      )}
                    </div>
                    
                    {/* Workbook Upload Section */}
                    <div className="border-2 border-dashed border-border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium">Workbook PDF</span>
                      </div>
                      
                      {course.workbook_url ? (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Workbook uploaded</span>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" asChild>
                              <a href={course.workbook_url} target="_blank" rel="noopener noreferrer">
                                <Download className="h-3 w-3" />
                              </a>
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleFileUpload(file, course.id, 'workbook');
                            }}
                            className="hidden"
                            id={`workbook-${course.id}`}
                          />
                          <label htmlFor={`workbook-${course.id}`}>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="w-full"
                              disabled={uploadingWorkbook}
                              asChild
                            >
                              <span>
                                <Upload className="h-3 w-3 mr-1" />
                                {uploadingWorkbook ? 'Uploading...' : 'Upload Workbook'}
                              </span>
                            </Button>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Courses;